/**
 * Hero — sage-tinted band at the top of the page. Variant A layout: title +
 * subtitle + by-line on the left, Foundations promo card with circular seal
 * on the right. Below 768px the card stacks under the text.
 *
 * Why the Foundations card lives here: non-DC visitors who don't engage
 * with the calculator never scroll past the hero. Putting Foundations in
 * the first viewport keeps the marketing surface visible without demoting
 * the calculator.
 */

import { el } from "../dom";
import { MARKETING } from "../marketing";

import "./hero.css";

export interface HeroHandle {
  el: HTMLElement;
}

/**
 * Build the small circular Foundations seal inline as SVG. Same shape as the
 * banner's bigger seal but rendered at ~88px so it fits next to text.
 */
function buildHeroSeal(textLines: readonly string[]): SVGSVGElement {
  const svgNS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNS, "svg");
  svg.setAttribute("viewBox", "0 0 200 200");
  svg.setAttribute("class", "hero__seal-svg");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const outer = document.createElementNS(svgNS, "circle");
  outer.setAttribute("cx", "100");
  outer.setAttribute("cy", "100");
  outer.setAttribute("r", "96");
  outer.setAttribute("fill", "none");
  outer.setAttribute("stroke", "currentColor");
  outer.setAttribute("stroke-width", "2");
  svg.appendChild(outer);

  const inner = document.createElementNS(svgNS, "circle");
  inner.setAttribute("cx", "100");
  inner.setAttribute("cy", "100");
  inner.setAttribute("r", "70");
  inner.setAttribute("fill", "none");
  inner.setAttribute("stroke", "currentColor");
  inner.setAttribute("stroke-width", "1");
  svg.appendChild(inner);

  const lineHeight = 18;
  const startY = 100 - ((textLines.length - 1) * lineHeight) / 2;
  textLines.forEach((line, idx) => {
    const text = document.createElementNS(svgNS, "text");
    text.setAttribute("x", "100");
    text.setAttribute("y", String(startY + idx * lineHeight));
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("dominant-baseline", "middle");
    // SVG attributes don't resolve CSS var(); hardcode the display font stack.
    text.setAttribute(
      "font-family",
      '"ITC Avant Garde", "Avant Garde", "Century Gothic", "Futura", sans-serif',
    );
    text.setAttribute("font-size", "12");
    text.setAttribute("letter-spacing", "1.5");
    text.setAttribute("fill", "currentColor");
    text.textContent = line;
    svg.appendChild(text);
  });

  return svg;
}

export function createHero(): HeroHandle {
  const card = MARKETING.hero.foundationsCard;

  const heroText = el(
    "div",
    { class: "hero__text" },
    el("h1", { class: "hero__title", id: "hero-title" }, MARKETING.hero.title),
    el("p", { class: "hero__subtitle" }, MARKETING.hero.subtitle),
    el("p", { class: "hero__credit" }, MARKETING.hero.credit),
  );

  const heroCard = el(
    "aside",
    {
      class: "hero__card",
      "aria-labelledby": "hero-card-title",
    },
    el(
      "div",
      { class: "hero__card-text" },
      el("p", { class: "hero__card-eyebrow" }, card.eyebrow),
      el("p", { class: "hero__card-title", id: "hero-card-title" }, card.title),
      el("p", { class: "hero__card-body" }, card.body),
      el(
        "a",
        {
          href: card.ctaUrl,
          class: "hero__card-cta",
          target: "_blank",
          rel: "noopener noreferrer",
        },
        card.ctaLabel,
      ),
    ),
    el("div", { class: "hero__card-seal" }, buildHeroSeal(card.sealText)),
  );

  const root = el(
    "section",
    { class: "hero", "aria-labelledby": "hero-title" },
    el("div", { class: "container hero__inner" }, heroText, heroCard),
  );

  return { el: root };
}
